import { mode } from './interfaces/mode';
import { IRawSettings } from './interfaces/IRawSettings';
import { smsFormats } from './enums/sms-formats';
import { rawModes } from './enums/raw-modes';
import { rawOptions } from './enums/raw-options';
import { motionSensorModes } from './enums/motion-sensor-modes';
import { alarmActions } from './enums/alarm-actions';
import { motionSensorAmpModes } from './enums/motion-sensor-amp-modes';
import {
    IAlarmState,
    IDnsState,
    IServerState,
    ISettings,
    ISimState,
} from './interfaces/ISettings';
import { rawMode } from './interfaces/raw-mode';
import { rawOption } from './interfaces/raw-option';
import { smsFormat } from './interfaces/sms-format';
import { motionSensorMode } from './interfaces/motion-sensor-mode';
import { alarmAction } from './interfaces/alarm-action';
import { motionSensorAmpMode } from './interfaces/motion-sensor-amp-mode';

export default class Settings {
    public mode: mode;
    public settings: Partial<ISettings>;
    public modes: rawMode[] = Object.values(rawModes);
    public options: rawOption[] = Object.values(rawOptions);
    public motionSensorModes: motionSensorMode[] =
        Object.values(motionSensorModes);
    public alarmActions: alarmAction[] = Object.values(alarmActions);
    public motionSensorAmpModes: motionSensorAmpMode[] =
        Object.values(motionSensorAmpModes);
    public smsFormats: smsFormat[] = Object.values(smsFormats);

    // Ключ - то что на устройстве, значение - то что в коде
    public static optionsMap = {
        tmp: 'coordTxPeriod',
        pmat: 'periodicActiveTime',
        pmst: 'periodicSleepTime',
        pmglp: 'periodicLocationPeriod',
        accm: 'motionSensorMode',
        accps: 'periodStart',
        accpe: 'periodEnd',
        accas: 'accelerometerAmp',
        accit: 'ignoreTime',
        presndalrs: 'preliminaryAlarmSend',
        smss: 'smsState',
        smsf: 'smsFormat',
        s1pn: 'phoneNumSim1',
        s2pn: 'phoneNumSim2',
        msc: 'mainSim',
        tsgnss: 'timeSyncSatsState',
        tsgsm: 'timeSyncGsmState',
        tsntp: 'timeSyncNtpState',
        ntpsa: 'ntpAddress',
        ntpsp: 'ntpPort',
        gnssm: 'gnssPowersave',
        gnssmint: 'gnssMinSearchTime',
        gnssmaxt: 'gnssMaxSearchTime',
        gnssrt: 'gnssRectificationTime',
        ftpsa: 'ftpAddress',
        ftpsp: 'ftpPort',
        ftpl: 'ftpLogin',
        ftpp: 'ftpPassword',
        ftpd: 'ftpDirectory',
        ftpfn: 'ftpFilename',
        ledm: 'ledState',
        rtctz: 'deviceTimezone',

        lsm: 'lsm',
        lssm: 'lssm',
        lsps: 'lsps',
        lspe: 'lspe',
        lsit: 'lsit',

        tsm: 'tsm',
        tsps: 'tsps',
        tspe: 'tspe',
        tsa: 'tsa',
        tsrt: 'tsaht',
        tsit: 'tsit',
    };

    constructor(settings: IRawSettings) {
        this.mode = this.getMode(settings);
        this.settings = this.getSettings(settings);
    }

    public getMode(settings: IRawSettings): mode {
        const alarmRegExp = /^(alarm)(\d)*s$/;
        const keys = Object.keys(settings);
        const alarmArr = keys.filter((item) => alarmRegExp.test(item));
        if (settings.tms === 1 || settings.tms === '1') return 'tracker';
        if (settings.pms === 1 || settings.pms === '1') return 'periodic';
        for (let item of alarmArr) {
            if (settings[item] === 1 || settings[item] === '1') return 'alarm';
        }
        return 'periodic';
    }

    public getSettings(settings: IRawSettings): Partial<ISettings> {
        const result: Partial<ISettings> = {
            alarmState: {},
            wakeUpTime: {},
            wakeUpActiveTime: {},
            alarmAction: {},
            serverState: {},
            serverProtocol: {},
            serverAddress: {},
            serverPort: {},
            serverPassword: {},
            simApn: {},
            simLogin: {},
            simPassword: {},
            dnsAddress: {},
        };
        const alarmRegExp = /^(alarm)(\d)*s$/;
        const alarmWakeRegExp = /^(alarm)(\d)*t$/;
        const alarmActiveRegExp = /^(alarm)(\d)*wt$/;
        const alarmActionRegExp = /^(alarm)(\d)*a$/;
        const serverStateRegExp = /^(srv)(\d)*s$/;
        const serverProtocolRegExp = /^(srv)(\d)*p$/;
        const serverAddressRegExp = /^(srv)(\d)*wipsa$/;
        const serverPortRegExp = /^(srv)(\d)*wipsp$/;
        const serverPasswordRegExp = /^(srv)(\d)*wipspass$/;
        const simApnRegExp = /^(s)(\d)*(apn)$/;
        const simLoginRegExp = /^(s)(\d)*(login)$/;
        const simPasswordRegExp = /^(s)(\d)*(pass)$/;
        const dnsRegExp = /^(dns)(\d)*$/;
        for (let key in settings) {
            if (Settings.optionsMap.hasOwnProperty(key)) {
                const name =
                    Settings.optionsMap[
                        key as keyof typeof Settings.optionsMap
                    ];
                result[name as keyof typeof result] = settings[
                    key as keyof IRawSettings
                ] as never;
            } else if (alarmRegExp.test(key)) {
                const res = key.match(alarmRegExp);
                if (res) {
                    const num = res[2];
                    const _key = `alarm${num}` as keyof IAlarmState<boolean>;
                    result.alarmState![_key] =
                        settings[key] === 1 || settings[key] === '1';
                }
            } else if (alarmWakeRegExp.test(key)) {
                const res = key.match(alarmWakeRegExp);
                if (res) {
                    const num = res[2];
                    const _key = `alarm${num}` as keyof IAlarmState<
                        string | number
                    >;
                    result.wakeUpTime![_key] = parseInt(
                        String(settings[key]),
                        10,
                    );
                }
            } else if (alarmActiveRegExp.test(key)) {
                const res = key.match(alarmActiveRegExp);
                if (res) {
                    const num = res[2];
                    const _key = `alarm${num}` as keyof IAlarmState<
                        string | number
                    >;
                    result.wakeUpActiveTime![_key] = settings[key] as
                        | string
                        | number;
                }
            } else if (alarmActionRegExp.test(key)) {
                const res = key.match(alarmActionRegExp);
                if (res) {
                    const num = res[2];
                    const _key = `alarm${num}` as keyof IAlarmState<
                        string | number
                    >;
                    result.alarmAction![_key] = settings[key] as
                        | string
                        | number;
                }
            } else if (serverStateRegExp.test(key)) {
                const res = key.match(serverStateRegExp);
                if (res) {
                    const num = res[2];
                    const _key = `server${num}` as keyof IServerState<boolean>;
                    result.serverState![_key] =
                        settings[key] === 1 || settings[key] === '1';
                }
            } else if (serverProtocolRegExp.test(key)) {
                const res = key.match(serverProtocolRegExp);
                if (res) {
                    const num = res[2];
                    const _key = `server${num}` as keyof IServerState<string>;
                    result.serverProtocol![_key] = settings[key] as string;
                }
            } else if (serverAddressRegExp.test(key)) {
                const res = key.match(serverAddressRegExp);
                if (res) {
                    const num = res[2];
                    const _key = `server${num}` as keyof IServerState<string>;
                    result.serverAddress![_key] = settings[key] as string;
                }
            } else if (serverPortRegExp.test(key)) {
                const res = key.match(serverPortRegExp);
                if (res) {
                    const num = res[2];
                    const _key = `server${num}` as keyof IServerState<
                        string | number
                    >;
                    result.serverPort![_key] = settings[key] as string | number;
                }
            } else if (serverPasswordRegExp.test(key)) {
                const res = key.match(serverPasswordRegExp);
                if (res) {
                    const num = res[2];
                    const _key = `server${num}` as keyof IServerState<string>;
                    result.serverPassword![_key] = settings[key] as string;
                }
            } else if (simApnRegExp.test(key)) {
                const res = key.match(simApnRegExp);
                if (res) {
                    const num = res[2];
                    const _key = `sim${num}` as keyof ISimState<string>;
                    result.simApn![_key] = settings[key] as string;
                }
            } else if (simLoginRegExp.test(key)) {
                const res = key.match(simLoginRegExp);
                if (res) {
                    const num = res[2];
                    const _key = `sim${num}` as keyof ISimState<string>;
                    result.simLogin![_key] = settings[key] as string;
                }
            } else if (simPasswordRegExp.test(key)) {
                const res = key.match(simPasswordRegExp);
                if (res) {
                    const num = res[2];
                    const _key = `sim${num}` as keyof ISimState<string>;
                    result.simPassword![_key] = settings[key] as string;
                }
            } else if (dnsRegExp.test(key)) {
                const res = key.match(dnsRegExp);
                if (res) {
                    const num = res[2];
                    const _key = `dns${num}` as keyof IDnsState<string>;
                    result.dnsAddress![_key] = settings[key] as string;
                }
            }
        }
        return result;
    }

    public static encodeSettings(settings: ISettings): IRawSettings {
        const result: IRawSettings = {};
        const keys = Object.keys(Settings.optionsMap);
        const values = Object.values(Settings.optionsMap);
        for (let key in settings) {
            let val = settings[key as keyof ISettings];
            if (key === 'mode') {
                result.tms = 0;
                result.pms = 0;
                result.alarm1s = 0;
                result.alarm2s = 0;
                result.alarm3s = 0;
                result.alarm4s = 0;
                result.alarm5s = 0;
                result.alarm6s = 0;
                if (val === 'tracker') result.tms = 1;
                if (val === 'periodic') result.pms = 1;
                if (val === 'alarm') {
                    result.alarm1s = settings.alarmState.alarm1 ? 1 : 0;
                    result.alarm2s = settings.alarmState.alarm2 ? 1 : 0;
                    result.alarm3s = settings.alarmState.alarm3 ? 1 : 0;
                    result.alarm4s = settings.alarmState.alarm4 ? 1 : 0;
                    result.alarm5s = settings.alarmState.alarm5 ? 1 : 0;
                    result.alarm6s = settings.alarmState.alarm6 ? 1 : 0;
                }
            } else if (key === 'wakeUpTime' && typeof val === 'object') {
                for (let k in val) {
                    result[`${k}t`] = val[k as keyof typeof val];
                }
            } else if (key === 'wakeUpActiveTime' && typeof val === 'object') {
                for (let k in val) {
                    result[`${k}wt`] = val[k as keyof typeof val];
                }
            } else if (key === 'alarmAction' && typeof val === 'object') {
                for (let k in val) {
                    result[`${k}a`] = val[k as keyof typeof val];
                }
            } else if (key === 'serverState' && typeof val === 'object') {
                for (let k in val) {
                    const index = k.split('server')[1];
                    result[`srv${index}s`] = val[k as keyof typeof val] ? 1 : 0;
                }
            } else if (key === 'serverProtocol' && typeof val === 'object') {
                for (let k in val) {
                    const index = k.split('server')[1];
                    result[`srv${index}p`] = val[k as keyof typeof val];
                }
            } else if (key === 'serverAddress' && typeof val === 'object') {
                for (let k in val) {
                    const index = k.split('server')[1];
                    result[`srv${index}wipsa`] = val[k as keyof typeof val];
                }
            } else if (key === 'serverPort' && typeof val === 'object') {
                for (let k in val) {
                    const index = k.split('server')[1];
                    result[`srv${index}wipsp`] = val[k as keyof typeof val];
                }
            } else if (key === 'serverPassword' && typeof val === 'object') {
                for (let k in val) {
                    const index = k.split('server')[1];
                    result[`srv${index}wipspass`] = val[k as keyof typeof val];
                }
            } else if (key === 'simApn' && typeof val === 'object') {
                for (let k in val) {
                    const index = k.split('sim')[1];
                    result[`s${index}apn`] = val[k as keyof typeof val];
                }
            } else if (key === 'simLogin' && typeof val === 'object') {
                for (let k in val) {
                    const index = k.split('sim')[1];
                    result[`s${index}login`] = val[k as keyof typeof val];
                }
            } else if (key === 'simPassword' && typeof val === 'object') {
                for (let k in val) {
                    const index = k.split('sim')[1];
                    result[`s${index}pass`] = val[k as keyof typeof val];
                }
            } else if (key === 'dnsAddress' && typeof val === 'object') {
                for (let k in val) {
                    const index = k.split('dns')[1];
                    result[`dns${index}`] = val[k as keyof typeof val];
                }
            } else {
                const index = values.indexOf(key);
                if (index > -1) {
                    const k = keys[index];
                    if (typeof val === 'boolean') {
                        val = val ? 1 : 0;
                    }
                    result[k] = val;
                }
            }
        }
        return result;
    }
}
